import { useState, useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import cn from 'classnames'
import robotImg from 'features/main/robot/assets/images/robot.png'
import c from './onboarding.module.scss'

const SLIDES = [
    { key: 'slide1', type: 'welcome' },
    { key: 'slide2', robot: robotImg, type: 'hint', highlight: 'coins', placement: 'mid' },
    { key: 'slide3', robot: robotImg, type: 'hint', highlight: 'modes', placement: 'aboveTabs' },
    { key: 'slide4', robot: robotImg, type: 'hint', highlight: 'earnings', placement: 'mid' },
    { key: 'slide5', robot: robotImg, type: 'hint', highlight: 'leaderboard', placement: 'mid' },
    { key: 'slide6', robot: robotImg, type: 'hint', highlight: 'shop', placement: 'aboveTabs' },
    { key: 'slide7', robot: robotImg, type: 'hint', highlight: 'profile', placement: 'belowHeader' },
]

const RAY_PATHS = [
    'M284.83 364.84C260.83 373.99 235.54 379.31 209.89 380.61L197.79 136.52Z',
    'M117.45 367.47C93.2 359.07 70.42 346.92 49.95 331.45L197.79 136.52Z',
    'M-12.33 262.03C-25.49 240.02-35.1 216.08-40.81 191.08L197.79 136.52Z',
    'M-43.8 97.86C-39.7 72.54-31.65 48.01-19.93 25.18L197.79 136.52Z',
    'M37.78-48.22C57.21-65.01 79.16-78.64 102.83-88.62L197.79 136.52Z',
    'M194.23-107.85C219.91-108.26 245.48-104.62 270.02-97.08L197.79 136.52Z',
    'M352.35-53.15C372.26-36.97 389.49-17.76 403.41 3.77L197.79 136.52Z',
    'M438.14 90.31C442.97 115.49 443.8 141.28 440.59 166.73L197.79 136.52Z',
    'M411.48 255.39C398.96 277.79 383 298.1 364.17 315.56L197.79 136.52Z',
]

const Sunburst = () => (
    <svg viewBox="0 0 402 381" fill="none" xmlns="http://www.w3.org/2000/svg">
        <defs>
            <radialGradient
                id="ob-rays"
                cx="0" cy="0" r="1"
                gradientUnits="userSpaceOnUse"
                gradientTransform="translate(196 137) rotate(159) scale(243 244)"
            >
                <stop stopColor="#FF6161" />
                <stop offset="1" stopColor="#1B1C24" stopOpacity="0" />
            </radialGradient>
        </defs>
        {RAY_PATHS.map((d, i) => (
            <path key={i} d={d} fill="url(#ob-rays)" fillOpacity="0.5" />
        ))}
    </svg>
)

export const Onboarding = ({ onComplete }) => {
    const { t } = useTranslation()
    const [current, setCurrent] = useState(0)

    const finish = useCallback(() => {
        localStorage.setItem('onboardingCompleted', 'true')
        document.body.removeAttribute('data-onboarding')
        onComplete()
    }, [onComplete])

    useEffect(() => {
        const highlight = SLIDES[current].highlight || 'none'
        document.body.setAttribute('data-onboarding', highlight)
        return () => document.body.removeAttribute('data-onboarding')
    }, [current])

    const handleNext = () => {
        if (current < SLIDES.length - 1) {
            setCurrent(current + 1)
        } else {
            finish()
        }
    }

    const slide = SLIDES[current]
    const isWelcome = slide.type === 'welcome'

    return (
        <>
            <div
                className={c.overlay}
                onClick={isWelcome ? undefined : handleNext}
            />

            {!isWelcome && (
                <button
                    className={c.skip}
                    onClick={(e) => {
                        e.stopPropagation()
                        finish()
                    }}
                >
                    {t('onboarding.skip')}
                </button>
            )}

            {isWelcome ? (
                <div className={c.welcomePanel}>
                    <div className={c.welcomeRobot}>
                        <img src={robotImg} alt="" />
                    </div>
                    <div className={c.welcomeSheet}>
                        <div className={c.sunburst} aria-hidden>
                            <Sunburst />
                        </div>
                        <div className={c.sheetToggle} />
                        <h2 className={c.welcomeTitle}>
                            {t(`onboarding.${slide.key}.title`)}
                        </h2>
                        <p className={c.welcomeText}>
                            {t(`onboarding.${slide.key}.text`)}
                        </p>
                        <button className={c.startButton} onClick={handleNext}>
                            {t('onboarding.start')}
                        </button>
                        <div className={c.dots}>
                            {SLIDES.map((_, i) => (
                                <div
                                    key={i}
                                    className={cn(c.dot, { [c.active]: i === current })}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            ) : (
                <div
                    key={slide.key}
                    className={cn(
                        c.hintPanel,
                        slide.placement && c[`placement_${slide.placement}`]
                    )}
                >
                    <div className={c.hintRow}>
                        <div className={c.hintCard}>
                            <div className={c.hintTitle}>
                                {t(`onboarding.${slide.key}.title`)}
                            </div>
                            <div className={c.hintText}>
                                {t(`onboarding.${slide.key}.text`)}
                            </div>
                        </div>
                        <div className={c.hintAvatarWrap}>
                            <div className={c.hintAvatarBg} />
                            <img className={c.hintAvatar} src={slide.robot} alt="" />
                        </div>
                    </div>
                    <div className={c.dots}>
                        {SLIDES.map((_, i) => (
                            <div
                                key={i}
                                className={cn(c.dot, { [c.active]: i === current })}
                            />
                        ))}
                    </div>
                </div>
            )}
        </>
    )
}
